import React, { useEffect, useRef, Suspense } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { useObjectStore } from "../zustand/objects";
import {PreviewModel, ShapeTexture, PreviewAIModel, UIModelTexture, PreviewUploadedModel} from "./PreviewModel";
import {PreviewFigmaObject} from "./FigmaObject";
import PreviewText from "./PreviewText";
import PreviewImageObject from "./PreviewImageObject";
import "../dot.css";
import {
  VIEWPORT_SHADOW_MAP,
  getViewportSceneTheme,
} from "../utils/viewportCanvasDefaults";
import { MBX_DARK_BACKGROUND_FILL } from "../styleFile";
import { AtmosphereSky } from "./AtmosphereSky";
import { AnimatedSceneBackground } from "./AnimatedSceneBackground";
import { ViewportSunLights } from "./ViewportSunLights";

/** Realtime JSON uses plain arrays; local sends may use THREE-like objects. */
function guestPositionVec3(pos) {
  if (pos == null) return { x: 0, y: 1.75, z: 0 };
  if (Array.isArray(pos)) {
    const x = Number(pos[0]);
    const y = pos[1];
    const z = Number(pos[2]);
    return {
      x: Number.isFinite(x) ? x : 0,
      y: y !== undefined && y !== null && Number.isFinite(Number(y)) ? Number(y) : 1.75,
      z: Number.isFinite(z) ? z : 0,
    };
  }
  const x = Number(pos.x);
  const y = Number(pos.y);
  const z = Number(pos.z);
  return {
    x: Number.isFinite(x) ? x : 0,
    y: Number.isFinite(y) ? y : 1.75,
    z: Number.isFinite(z) ? z : 0,
  };
}

function guestRotationEuler(rot) {
  if (rot == null) return { x: 0, y: 0, z: 0 };
  if (Array.isArray(rot)) {
    const x = Number(rot[0]);
    const y = Number(rot[1]);
    const z = Number(rot[2]);
    return {
      x: Number.isFinite(x) ? x : 0,
      y: Number.isFinite(y) ? y : 0,
      z: Number.isFinite(z) ? z : 0,
    };
  }
  const x = Number(rot._x ?? rot.x);
  const y = Number(rot._y ?? rot.y);
  const z = Number(rot._z ?? rot.z);
  return {
    x: Number.isFinite(x) ? x : 0,
    y: Number.isFinite(y) ? y : 0,
    z: Number.isFinite(z) ? z : 0,
  };
}

export const ScreenshotGuest = ({ artboard, cameraGuest, id }) => {

  const canvasRef = useRef()

  const {
    artboards,
    objectsAreLoaded,
    currentObjectArtboard, 
    floorColour,
    updatePreviewCamera,
    viewportThemeOverride,
  } = useObjectStore();

  // const others = [];

  // useEffect(() => {
  //   console.log(cameraGuest.position)
  // },[cameraGuest.position.z])


  useEffect(() => {
    updatePreviewCamera('position', [0,0,0])
    updatePreviewCamera('rotation', [0,0,0])
    
  }, [updatePreviewCamera])

  const initialP = guestPositionVec3(cameraGuest?.position);
  const initialR = guestRotationEuler(cameraGuest?.rotation);
  const sceneTheme = getViewportSceneTheme(viewportThemeOverride);
  const guestCanvasBackground =
    viewportThemeOverride === "dark" ? MBX_DARK_BACKGROUND_FILL : sceneTheme.background;

  function PreviewCameraFunction() {
    const { camera, invalidate } = useThree();
    useFrame(() => {
      const p = guestPositionVec3(cameraGuest.position);
      const r = guestRotationEuler(cameraGuest.rotation);
      camera.position.set(p.x, p.y, p.z);
      camera.rotation.set(r.x, r.y, r.z);
      invalidate();
    });
    return null;
  }

    return <Suspense fallback={<div><img src="/image/loading.gif" alt="" style={{ width: "50px", height: "50px" }} /></div>}>

      <>
    <Canvas
      style={{
        top: 0, 
        left: 0,
        width: "100%",
        height: "100%",
        // height: "auto",
        position: 'relative'
      }}
      camera={{
        position: [initialP.x, initialP.y, initialP.z + 2],
        rotation: [initialR.x, initialR.y, initialR.z],
        fov: 55
      }}
      frameloop="demand"
      shadows
      gl={{ preserveDrawingBuffer: true }}
      ref={canvasRef}
      >
      
      {(cameraGuest?.position != null || cameraGuest?.rotation != null) && <PreviewCameraFunction />}
      <AnimatedSceneBackground color={guestCanvasBackground} />
      <AtmosphereSky {...sceneTheme.skyAtmosphere} />
      <ViewportSunLights
        ambientIntensity={sceneTheme.ambientIntensity}
        directionalIntensity={sceneTheme.directionalIntensity}
        directionalColor={sceneTheme.directionalColor}
        shadowMapSize={VIEWPORT_SHADOW_MAP}
      />

      {artboards[artboard] && artboards[artboard].map((mapped, i) => (
        mapped.category !== "text" ?
          (mapped.object === "camera")
            ? ''
            : (mapped.category === "image") ? 
            <PreviewImageObject
              key={i}
              position={mapped.position}
              rotation={mapped.rotation}
              scale={mapped.scale}
              id={mapped.id}
              destination={mapped.destination}
              object={mapped.object}
              category={mapped.category}
              currentObjectArtboard={currentObjectArtboard}
              objectsAreLoaded={objectsAreLoaded}
              colour={mapped.colour}
              pose={mapped.pose}
              image={mapped.image}
              imageSize={mapped.imageSize}
              matrixState={mapped.matrixState}
              curved={mapped.curved}
              curveAmount={mapped.curveAmount}
            />  : (mapped.category === "figma") ?
            <PreviewFigmaObject
                  key={i}
                  position={mapped.position}
                  rotation={mapped.rotation}
                  scale={mapped.scale}
                  id={mapped.id}
                  destination={mapped.destination}
                  object={mapped.object}
                  category={mapped.category}
                  currentObjectArtboard={1}
                  colour={mapped.colour}
                  figmaID={mapped.figmaID}
                  figmaURL={mapped.figmaURL}
                  figmaSize={mapped.figmaSize}
                  matrixState={mapped.matrixState}
                  curved={mapped.curved}
                  curveAmount={mapped.curveAmount}
                /> :
                            mapped.databaseId && (mapped.category === "ai") ?
            <PreviewAIModel
            key={mapped.id}
            position={mapped.position}
            rotation={mapped.rotation}
            scale={mapped.scale}
            id={mapped.id}
            destination={mapped.destination}
            object={mapped.object}
            prompt={mapped.object}
            category={mapped.category}
            url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
            artboard={artboards[currentObjectArtboard]}
            currentObjectArtboard={currentObjectArtboard}
            objectsAreLoaded={objectsAreLoaded}
            colour={mapped.colour}
            pose={mapped.pose}
            imageTexture={mapped.imageTexture}
            isLocked={mapped.isLocked}
            matrixState={mapped.matrixState}
            databaseId={mapped.databaseId}
            databaseURL={mapped.databaseURL}
          />  :
          mapped.uploadedURL && (mapped.category === "uploaded") ?
            <PreviewUploadedModel
            key={mapped.id}
            position={mapped.position}
            rotation={mapped.rotation}
            scale={mapped.scale}
            id={mapped.id}
            destination={mapped.destination}
            object={mapped.object}
            prompt={mapped.object}
            category={mapped.category}
            url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
            artboard={artboards[currentObjectArtboard]}
            currentObjectArtboard={currentObjectArtboard}
            objectsAreLoaded={objectsAreLoaded}
            colour={mapped.colour}
            pose={mapped.pose}
            imageTexture={mapped.imageTexture}
            isLocked={mapped.isLocked}
            matrixState={mapped.matrixState}
            uploadedURL={mapped.uploadedURL}
            uploadId={mapped.uploadId}
          />  :
          mapped.imageTexture && (mapped.category === "UI") ?
          <UIModelTexture
              key={mapped.id}
              position={mapped.position}
              rotation={mapped.rotation}
              scale={mapped.scale}
              id={mapped.id}
              destination={mapped.destination}
              object={mapped.object}
              category={mapped.category}
              url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
              artboard={artboards[currentObjectArtboard]}
              currentObjectArtboard={currentObjectArtboard}
              objectsAreLoaded={objectsAreLoaded}
              colour={mapped.colour}
              pose={mapped.pose}
              imageTexture={mapped.imageTexture}
              isLocked={mapped.isLocked}
              matrixState={mapped.matrixState}
              curved={mapped.curved}
              curveAmount={mapped.curveAmount}
            />
            :
            (mapped.imageTexture && mapped.category === "shape") ?
      <ShapeTexture
      key={i}
      position={mapped.position}
      rotation={mapped.rotation}
      scale={mapped.scale}
      id={mapped.id}
      destination={mapped.destination}
      object={mapped.object}
      category={mapped.category}
      url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
      artboard={artboards[currentObjectArtboard]}
      currentObjectArtboard={currentObjectArtboard}
      objectsAreLoaded={objectsAreLoaded}
      colour={mapped.colour}
      pose={mapped.pose}
      imageTexture={mapped.imageTexture}
      matrixState={mapped.matrixState}
    /> :
            <PreviewModel 
              key={i}
              position={mapped.position}
              rotation={mapped.rotation}
              scale={mapped.scale}
              id={mapped.id}
              destination={mapped.destination}
              object={mapped.object}
              category={mapped.category}
              url={"/model/" + mapped.category + "/" + mapped.object + ".glb"}
              artboard={artboards[artboard]}
              currentObjectArtboard={artboard}
              objectsAreLoaded={objectsAreLoaded}
              colour={mapped.colour}
              pose={mapped.pose}
              matrixState={mapped.matrixState}
              curved={mapped.curved}
              curveAmount={mapped.curveAmount}
              imageTexture={mapped.imageTexture}
              modelPath={mapped.modelPath}
            />
          :
          <PreviewText
            key={i}
            content={mapped.content}
            position={mapped.position}
            rotation={mapped.rotation}
            scale={mapped.scale}
            id={mapped.id}
            destination={mapped.destination}
            artboard={artboards[artboard]}
            currentObjectArtboard={artboard}
            objectsAreLoaded={objectsAreLoaded}
            colour={mapped.colour}
            bGColour={mapped.bGColour}
            matrixState={mapped.matrixState}
          />
      ))}


      <mesh
        receiveShadow
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, 0]}
      >
        <planeGeometry attach="geometry" args={[50, 50]} />
        <meshStandardMaterial
              color={floorColour}
              attach="material"
              opacity={1}
            />
          </mesh>
    </Canvas>
    </>
    </Suspense>
  }
 


